"use client";

import { useState, forwardRef } from "react";
import { InputHTMLAttributes } from "react";

import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cva } from "class-variance-authority";
import { LuCircleAlert, LuSearch, LuX, LuPhone } from "react-icons/lu";
import { BsPersonVcard } from "react-icons/bs";

import { cn } from "../../lib/utils";
import {
  CustomLabel,
  CustomLabelPropsType,
} from "@/components/common/customLabel";

import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { capitalizeWords } from "@/utils/helpers/textCase";
import { LINE_MAX_LENGTH } from "@/utils/sanitize/limits";
import {
  CONTROL_SIZE_TOKENS,
  type ControlSizeProp,
} from "@/components/ui/control-size";

const inputVariants = cva(
  `text-fg
  flex
  w-full
  bg-white
  border
  border-ring
  shadow-sm
  transition-colors
  file:border-0
  file:bg-transparent
  file:text-sm
  file:font-medium
  file:text-fg
  focus:outline-none
  focus:border-ring-focus
  placeholder:text-bg+3

  disabled:cursor-not-allowed
  disabled:bg-bg
  disabled:text-fg-muted

  [&::-webkit-outer-spin-button]:appearance-none
  [&::-webkit-inner-spin-button]:appearance-none
  [-moz-appearance:textfield]
  `,
  {
    variants: {
      size: {
        // "default" is an alias of "md", kept for old call sites.
        //
        // NOTE: the responsive text classes below (e.g. "lg:text-sm") are
        // written out in full on purpose, never built with a template
        // literal — Tailwind's build-time scanner only picks up class names
        // that appear as literal substrings in source, so a computed
        // `` `lg:${token.text}` `` would silently fail to generate any CSS.
        default: `${CONTROL_SIZE_TOKENS.md.height} ${CONTROL_SIZE_TOKENS.md.px} ${CONTROL_SIZE_TOKENS.md.py} ${CONTROL_SIZE_TOKENS.md.radius} text-base lg:text-sm`,
        // Real <input> elements use `text-base` on mobile (prevents iOS zoom
        // on focus) and switch to the token's font size from `lg:` up, where
        // that zoom concern no longer applies.
        xs: `${CONTROL_SIZE_TOKENS.xs.height} ${CONTROL_SIZE_TOKENS.xs.px} ${CONTROL_SIZE_TOKENS.xs.py} ${CONTROL_SIZE_TOKENS.xs.radius} text-base lg:text-xs placeholder:text-xs`,
        sm: `${CONTROL_SIZE_TOKENS.sm.height} ${CONTROL_SIZE_TOKENS.sm.px} ${CONTROL_SIZE_TOKENS.sm.py} ${CONTROL_SIZE_TOKENS.sm.radius} text-base lg:text-sm placeholder:text-sm`,
        md: `${CONTROL_SIZE_TOKENS.md.height} ${CONTROL_SIZE_TOKENS.md.px} ${CONTROL_SIZE_TOKENS.md.py} ${CONTROL_SIZE_TOKENS.md.radius} text-base lg:text-sm sm:placeholder:text-sm`,
        lg: `${CONTROL_SIZE_TOKENS.lg.height} ${CONTROL_SIZE_TOKENS.lg.px} ${CONTROL_SIZE_TOKENS.lg.py} ${CONTROL_SIZE_TOKENS.lg.radius} text-base sm:placeholder:text-base`,
        xl: `${CONTROL_SIZE_TOKENS.xl.height} ${CONTROL_SIZE_TOKENS.xl.px} ${CONTROL_SIZE_TOKENS.xl.py} ${CONTROL_SIZE_TOKENS.xl.radius} text-base sm:placeholder:text-base`,
      },
      variant: {
        default: "",
        destructive: "border-de+1 bg-de-3 placeholder:text-de+1 text-de+1",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> &
  CustomLabelPropsType & {
    className?: string;
    classNameParent?: string;
    variant?: "default" | "destructive";
    type?: string;
    error?: string;
    errorClassName?: string;
    touched?: boolean;
    isLoading?: boolean;
    size?: ControlSizeProp;
    searchIcon?: React.ReactNode;
    tooltip?: string;
    clearIcon?: boolean;
    onClear?: () => void;
    showPhoneInputSelector?: boolean;
    placeholder?: string;
    placeholderPhone?: string;
    inputType?: "phone" | "text";
    textTransform?:
      | "none"
      | "lowercase"
      | "uppercase"
      | "capitalize"
      | "sentence";
  };

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = "",
      classNameParent = "",
      variant,
      type,
      error,
      errorClassName,
      touched,
      isLoading,
      disabled,
      label,
      labelClassName,
      subLabelClassName,
      subLabel,
      labelTooltip,
      tooltip,
      searchIcon = false,
      size,
      clearIcon = false,
      onClear,
      textTransform = "none",
      icon,
      placeholder = "Digita",
      placeholderPhone = "Ingresa número",
      inputType = "text",
      showPhoneInputSelector = false,
      ...props
    },
    ref,
  ) => {
    const [inputTypeSelector, setInputTypeSelector] = useState<
      "phone" | "text"
    >(inputType);

    // Text inputs stop at the same length the database stores, and say so when they do:
    // nothing is cut without the user seeing it. Other types (password, number, date, file)
    // keep the browser behaviour.
    const isTextual = !type || ["text", "search", "email", "tel", "url"].includes(type);
    const lengthLimit = isTextual ? (props.maxLength ?? LINE_MAX_LENGTH) : undefined;
    const currentLength = typeof props.value === "string" ? props.value.length : 0;
    const atLengthLimit = lengthLimit !== undefined && currentLength >= lengthLimit;

    // <div className={`flex flex-1 flex-col gap-1 w-full ${classNameParent}`}>
    {
      /* <div className={`flex flex-col items-start flex-1 min-w-0`}></div> */
    }

    return (
      <div className={`flex flex-col gap-1 w-full ${classNameParent}`}>
        <div className={`flex flex-col items-start min-w-0`}>
          <CustomLabel
            icon={icon}
            label={label}
            labelClassName={labelClassName}
            labelTooltip={labelTooltip}
            subLabel={subLabel}
            subLabelClassName={subLabelClassName}
          />

          <div className="relative flex flex-col items-start w-full h-full">
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    "relative flex w-full items-end gap-1",
                    showPhoneInputSelector && "items-end",
                  )}
                >
                  <div className="relative min-w-0 flex-1">
                    {searchIcon && inputType !== "phone" && (
                      <LuSearch className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gr-3" />
                    )}

                    {/* ✅ PHONE MODE */}
                    {inputTypeSelector === "phone" ? (
                      <input
                        type="tel"
                        disabled={disabled}
                        value={props.value}
                        placeholder={placeholderPhone}
                        onChange={props.onChange}
                        onBlur={props.onBlur}
                        className={cn(
                          inputVariants({
                            variant: error && touched ? "destructive" : variant,
                            size,
                          }),
                          className,
                          searchIcon && "pl-[30px]",
                          clearIcon && "pr-9",
                          textTransform !== "none" &&
                            `text-transform-${textTransform}`,
                        )}
                        style={{
                          textTransform:
                            textTransform !== "none" &&
                            textTransform !== "sentence"
                              ? (textTransform as React.CSSProperties["textTransform"])
                              : undefined,
                        }}
                        autoComplete="tel"
                        inputMode="tel"
                        maxLength={lengthLimit}
                        ref={ref}
                      />
                    ) : (
                      // ✅ NORMAL INPUT (sin tocar lógica original)
                      <input
                        type={type}
                        disabled={disabled}
                        className={cn(
                          inputVariants({
                            variant: error && touched ? "destructive" : variant,
                            size,
                          }),
                          className,
                          searchIcon && "pl-[30px]",
                          clearIcon && "pr-9",
                          textTransform !== "none" &&
                            `text-transform-${textTransform}`,
                        )}
                        style={{
                          textTransform:
                            textTransform !== "none" &&
                            textTransform !== "sentence"
                              ? (textTransform as React.CSSProperties["textTransform"])
                              : undefined,
                        }}
                        autoComplete={`off-${Math.random()}`}
                        placeholder={placeholder}
                        ref={ref}
                        onInput={(e) => {
                          if (textTransform === "lowercase") {
                            e.currentTarget.value =
                              e.currentTarget.value.toLowerCase();
                          } else if (textTransform === "uppercase") {
                            e.currentTarget.value =
                              e.currentTarget.value.toUpperCase();
                          } else if (textTransform === "capitalize") {
                            e.currentTarget.value = capitalizeWords(
                              e.currentTarget.value,
                            );
                          } else if (textTransform === "sentence") {
                            const text = e.currentTarget.value.toLowerCase();
                            e.currentTarget.value =
                              text.charAt(0).toUpperCase() + text.slice(1);
                          }

                          if (props.onInput) {
                            props.onInput(e);
                          }
                        }}
                        {...props}
                        maxLength={lengthLimit}
                      />
                    )}

                    {isLoading ? (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 flex items-center justify-center text-gray-400 pointer-events-none">
                        <Spinner size={16} />
                      </span>
                    ) : (
                      clearIcon &&
                      props.value && (
                        <LuX
                          className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer flex-shrink-0 text-de hover:text-de+1"
                          onClick={onClear}
                        />
                      )
                    )}
                  </div>

                  {showPhoneInputSelector && (
                    <Tabs
                      defaultValue="text"
                      value={inputTypeSelector}
                      onValueChange={(value: string) => {
                        const typedValue = value as "text" | "phone";
                        setInputTypeSelector(typedValue);
                      }}
                      className="shrink-0"
                    >
                      <TabsList className="rounded-md" size={size ?? "md"}>
                        <TabsTrigger value="text" size={size ?? "md"}>
                          <BsPersonVcard />
                        </TabsTrigger>
                        <TabsTrigger value="phone" size={size ?? "md"}>
                          <LuPhone />
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  )}
                </div>
              </TooltipTrigger>

              {tooltip && <TooltipContent>{tooltip}</TooltipContent>}
            </Tooltip>

            {variant === "destructive" && (
              <span className="text-de+1 absolute right-3 top-1/2 transform -translate-y-1/2">
                <LuCircleAlert size={20} />
              </span>
            )}
          </div>

          {error && touched && (
            <p
              className={cn(
                "text-xs mt-1 font-normal",
                errorClassName || "text-de+1",
              )}
            >
              {error}
            </p>
          )}
          {atLengthLimit && (
            <p role="alert" className="text-xs mt-1 font-normal text-de+1">
              Máximo {lengthLimit} caracteres: el texto no puede ser más largo.
            </p>
          )}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input, inputVariants };
